import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

/**
 * Seat management (#609, Phase 1) — manual member provisioning + staff billing
 * setter mutations.
 *
 * `useProvisionMember` POSTs to the `enterprise-provision-member` EDGE FUNCTION
 * (not an RPC): the edge fn verifies the caller is a partner admin under their
 * JWT, resolves-or-creates the auth user, then calls the service-role
 * provisioning RPC. A `licensed` partner at its seat cap is rejected with a 409
 * whose JSON body carries `error: 'seat_cap_reached'` — supabase-js wraps any
 * non-2xx in a FunctionsHttpError with the real body on `error.context`
 * (a Response), so we parse it to surface the cap error distinctly (the dialog
 * shows the contact-us copy and stays open).
 *
 * `useSetPartnerBilling` calls the staff-only `enterprise_set_partner_billing`
 * RPC (gated `caller_can_manage_partner` — super_admin-only) used by the
 * super-admin Plan & seats control on PartnerDetail.
 */

/** Error subclass carrying the parsed edge-fn error code, so callers can branch. */
export class ProvisionMemberError extends Error {
  /** e.g. 'seat_cap_reached' | 'partner_disabled' | 'forbidden_target' | ... */
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'ProvisionMemberError';
    this.code = code;
  }
}

/** True when the provisioning failure was the licensed seat cap. */
export function isSeatCapError(err: unknown): boolean {
  return err instanceof ProvisionMemberError && err.code === 'seat_cap_reached';
}

/**
 * Error subclass for the activation edge fns (#615): `enterprise-activate-identity`
 * and `enterprise-send-activation-link`. Carries the parsed edge-fn error `code`
 * and, for send-activation-link, an optional `reason` (the `not_eligible`
 * sub-reason: already_active | identity_revoked | forbidden_target |
 * not_a_member | no_email).
 */
export class ActivationError extends Error {
  code: string;
  reason?: string;
  constructor(code: string, message: string, reason?: string) {
    super(message);
    this.name = 'ActivationError';
    this.code = code;
    this.reason = reason;
  }
}

/**
 * True when sending a first-login link failed because the target isn't eligible
 * (already active, revoked, not a member, no email, …). This is an expected
 * business outcome surfaced to the admin — not a bug — so it's gated out of
 * GlitchTip, mirroring isSeatCapError.
 */
export function isNotEligibleError(err: unknown): boolean {
  return err instanceof ActivationError && err.code === 'not_eligible';
}

/**
 * True for the expected 4xx business outcomes of activating one's own identity
 * (the member clicked a stale / invalid first-login link). User-facing states,
 * not bugs → not captured to GlitchTip.
 */
export function isExpectedActivationError(err: unknown): boolean {
  return (
    err instanceof ActivationError &&
    (err.code === 'not_a_member' ||
      err.code === 'forbidden_target' ||
      err.code === 'identity_revoked' ||
      err.code === 'partner_disabled')
  );
}

/**
 * Parse the supabase-js FunctionsHttpError (non-2xx) into an ActivationError.
 * The real payload ({ error, message, reason? }) lives on error.context (a
 * Response), exactly like useProvisionMember parses it.
 */
async function toActivationError(
  error: unknown,
  fallbackCode: string,
): Promise<ActivationError> {
  let code = fallbackCode;
  let message = (error as { message?: string })?.message || 'Activation failed';
  let reason: string | undefined;
  const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const parsed = (await ctx.json()) as {
        error?: string;
        message?: string;
        reason?: string;
      };
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.error === 'string' && parsed.error) code = parsed.error;
        if (typeof parsed.message === 'string' && parsed.message) message = parsed.message;
        if (typeof parsed.reason === 'string' && parsed.reason) reason = parsed.reason;
      }
    } catch {
      // Body wasn't JSON; keep the generic message.
    }
  }
  return new ActivationError(code, message, reason);
}

interface SendActivationLinkArgs {
  partner_id: string;
  user_id: string;
}

interface SendActivationLinkResult {
  sent: boolean;
  email: string;
}

/**
 * Admin action (#615): email a manually-added member a first-login link so they
 * can sign in (and thereby become emulatable). Invokes
 * `enterprise-send-activation-link` under the admin's JWT. A `not_eligible`
 * outcome (already active, revoked, no email, …) is an expected business result
 * — surfaced to the admin but NOT captured to GlitchTip.
 */
export function useSendActivationLink() {
  const qc = useQueryClient();
  return useMutation<SendActivationLinkResult, Error, SendActivationLinkArgs>({
    mutationFn: async ({ partner_id, user_id }) => {
      const { data, error } = await supabase.functions.invoke(
        'enterprise-send-activation-link',
        { body: { partner_id, user_id } },
      );
      if (error) throw await toActivationError(error, 'send_failed');
      return data as SendActivationLinkResult;
    },
    onSuccess: (_d, { partner_id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.memberList(partner_id) });
    },
    onError: (err) => {
      if (!isNotEligibleError(err)) {
        captureError(err as Error, { feature: 'enterprise-send-activation-link' });
      }
    },
  });
}

interface ActivateIdentityArgs {
  partner_id: string;
}

interface ActivateIdentityResult {
  status: 'activated' | 'already_active';
}

/**
 * Member self-activation (#615): the member clicks the first-login link and,
 * once authenticated, activates their own enterprise identity by invoking
 * `enterprise-activate-identity` under their own JWT (the server uses caller.id).
 * The expected 4xx business codes (not_a_member / forbidden_target /
 * identity_revoked) are user-facing states, not bugs → not captured.
 */
export function useActivateIdentity() {
  return useMutation<ActivateIdentityResult, Error, ActivateIdentityArgs>({
    mutationFn: async ({ partner_id }) => {
      const { data, error } = await supabase.functions.invoke(
        'enterprise-activate-identity',
        { body: { partner_id } },
      );
      if (error) throw await toActivationError(error, 'activation_failed');
      return data as ActivateIdentityResult;
    },
    onError: (err) => {
      if (!isExpectedActivationError(err)) {
        captureError(err as Error, { feature: 'enterprise-activate-identity' });
      }
    },
  });
}

interface ProvisionMemberArgs {
  partner_id: string;
  email: string;
}

interface ProvisionMemberResult {
  status: string;
  workspace_id: string | null;
  buildit_user_id: string | null;
}

export function useProvisionMember() {
  const qc = useQueryClient();
  return useMutation<ProvisionMemberResult, Error, ProvisionMemberArgs>({
    mutationFn: async ({ partner_id, email }) => {
      const { data, error } = await supabase.functions.invoke(
        'enterprise-provision-member',
        { body: { partner_id, email } },
      );
      if (error) {
        // supabase-js returns a FunctionsHttpError for any non-2xx; the real
        // payload ({ error, message }) lives on error.context (the Response).
        let code = 'provision_failed';
        let message = error.message || 'Provisioning failed';
        const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            if (parsed && typeof parsed === 'object') {
              if (typeof parsed.error === 'string' && parsed.error) code = parsed.error;
              if (typeof parsed.message === 'string' && parsed.message) message = parsed.message;
            }
          } catch {
            // Body wasn't JSON; keep the generic message.
          }
        }
        throw new ProvisionMemberError(code, message);
      }
      return data as ProvisionMemberResult;
    },
    onSuccess: (_d, { partner_id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.memberList(partner_id) });
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.seatUsage(partner_id) });
    },
    onError: (err) => {
      // seat_cap_reached is an expected business outcome (the partner is at its
      // licensed limit), surfaced to the user as "contact BuildIt" — not a bug.
      // Don't send it to GlitchTip; capture only genuine failures.
      if (!isSeatCapError(err)) {
        captureError(err as Error, { feature: 'enterprise-provision-member' });
      }
    },
  });
}

interface SetPartnerBillingArgs {
  partner_id: string;
  billing_model: 'licensed' | 'per_seat' | null;
  licensed_seats: number | null;
}

export function useSetPartnerBilling() {
  const qc = useQueryClient();
  return useMutation<void, Error, SetPartnerBillingArgs>({
    mutationFn: async ({ partner_id, billing_model, licensed_seats }) => {
      const { error } = await supabase.rpc('enterprise_set_partner_billing', {
        p_partner_id: partner_id,
        p_billing_model: billing_model,
        p_licensed_seats: licensed_seats,
      });
      if (error) throw error;
    },
    onSuccess: (_d, { partner_id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.detail(partner_id) });
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.list() });
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.seatUsage(partner_id) });
    },
    onError: (err) =>
      captureError(err as Error, { feature: 'enterprise-set-partner-billing' }),
  });
}
